export type NaturalFeedIdleTaskPriority = 'activate' | 'mount' | 'measure' | 'unmount'

type IdleWindow = Window &
    typeof globalThis & {
        requestIdleCallback?: (
            callback: IdleRequestCallback,
            options?: IdleRequestOptions,
        ) => number
        cancelIdleCallback?: (handle: number) => void
    }

type QueuedTask = {
    key: string
    priority: NaturalFeedIdleTaskPriority
    run: () => void
}

const SCROLL_SETTLE_MS = 120
const UNMOUNT_DEADLINE_MS = 250
const SCROLLING_UNMOUNT_TIMEOUT_MS = 1000
const PRIORITY_ORDER: Record<NaturalFeedIdleTaskPriority, number> = {
    activate: 0,
    mount: 1,
    measure: 2,
    unmount: 3,
}

export type NaturalFeedIdleTaskQueue = ReturnType<typeof createNaturalFeedIdleTaskQueue>

export function createNaturalFeedIdleTaskQueue(targetWindow: Window = window) {
    const idleWindow = targetWindow as IdleWindow
    const tasks = new Map<string, QueuedTask>()
    let scrolling = false
    let suspended = false
    let settleHandle: number | null = null
    let drainHandle: number | null = null
    let drainUsesIdleCallback = false
    let unmountDeadlineHandle: number | null = null

    const cancelUnmountDeadline = () => {
        if (unmountDeadlineHandle == null) return
        targetWindow.clearTimeout(unmountDeadlineHandle)
        unmountDeadlineHandle = null
    }

    const cancelDrain = () => {
        cancelUnmountDeadline()
        if (drainHandle == null) return
        if (drainUsesIdleCallback && typeof idleWindow.cancelIdleCallback === 'function') {
            idleWindow.cancelIdleCallback(drainHandle)
        } else {
            targetWindow.clearTimeout(drainHandle)
        }
        drainHandle = null
    }

    const hasTask = (priority: NaturalFeedIdleTaskPriority) => {
        for (const task of tasks.values()) {
            if (task.priority === priority) return true
        }
        return false
    }

    const takeNextTask = (onlyPriority?: NaturalFeedIdleTaskPriority) => {
        let next: QueuedTask | null = null
        tasks.forEach(task => {
            if (onlyPriority && task.priority !== onlyPriority) return
            if (!next || PRIORITY_ORDER[task.priority] < PRIORITY_ORDER[next.priority]) {
                next = task
            }
        })
        if (next) tasks.delete(next.key)
        return next
    }

    const scheduleDrain = () => {
        if (tasks.size === 0) {
            cancelUnmountDeadline()
            return
        }

        if (
            !scrolling &&
            typeof idleWindow.requestIdleCallback === 'function' &&
            unmountDeadlineHandle == null &&
            hasTask('unmount')
        ) {
            unmountDeadlineHandle = targetWindow.setTimeout(() => {
                unmountDeadlineHandle = null
                takeNextTask('unmount')?.run()
                scheduleDrain()
            }, UNMOUNT_DEADLINE_MS)
        }

        if (drainHandle != null) return

        const restrictToUnmounts = scrolling || suspended
        const canUnmountWhileRestricted = restrictToUnmounts && hasTask('unmount')
        if (restrictToUnmounts && !canUnmountWhileRestricted) return

        const drainOne = () => {
            drainHandle = null
            takeNextTask(restrictToUnmounts ? 'unmount' : undefined)?.run()
            if (!hasTask('unmount')) cancelUnmountDeadline()
            scheduleDrain()
        }

        if (typeof idleWindow.requestIdleCallback === 'function') {
            drainUsesIdleCallback = true
            drainHandle = idleWindow.requestIdleCallback(
                drainOne,
                scrolling ? { timeout: SCROLLING_UNMOUNT_TIMEOUT_MS } : undefined,
            )
        } else {
            drainUsesIdleCallback = false
            drainHandle = targetWindow.setTimeout(
                drainOne,
                scrolling ? SCROLLING_UNMOUNT_TIMEOUT_MS : 16,
            )
        }
    }

    const markIdle = () => {
        if (settleHandle != null) {
            targetWindow.clearTimeout(settleHandle)
            settleHandle = null
        }
        if (scrolling) cancelDrain()
        scrolling = false
        scheduleDrain()
    }

    const markScroll = (event?: Event) => {
        const supportsNativeScrollEnd =
            event?.currentTarget != null && 'onscrollend' in (event.currentTarget as HTMLElement)
        const isNativeScrollEvent = event?.type === 'scroll' && supportsNativeScrollEnd
        if (scrolling && supportsNativeScrollEnd) {
            // A wheel/touch event starts a fallback in case no scroll occurs. Once a native
            // scroll event arrives, scrollend owns settlement; leaving the fallback alive would
            // drain queued DOM work during touchpad momentum.
            if (isNativeScrollEvent && settleHandle != null) {
                targetWindow.clearTimeout(settleHandle)
                settleHandle = null
            }
            return
        }

        scrolling = true
        cancelDrain()
        if (settleHandle != null) targetWindow.clearTimeout(settleHandle)
        if (isNativeScrollEvent) {
            settleHandle = null
        } else {
            settleHandle = targetWindow.setTimeout(markIdle, SCROLL_SETTLE_MS)
        }
        scheduleDrain()
    }

    const enqueue = (key: string, priority: NaturalFeedIdleTaskPriority, run: () => void) => {
        tasks.set(key, { key, priority, run })
        scheduleDrain()
        return () => {
            tasks.delete(key)
            if (!hasTask('unmount')) cancelUnmountDeadline()
        }
    }

    const setSuspended = (nextSuspended: boolean) => {
        if (suspended === nextSuspended) return
        cancelDrain()
        suspended = nextSuspended
        scheduleDrain()
    }

    const dispose = () => {
        cancelDrain()
        if (settleHandle != null) targetWindow.clearTimeout(settleHandle)
        settleHandle = null
        cancelUnmountDeadline()
        tasks.clear()
        scrolling = false
        suspended = false
    }

    return {
        dispose,
        enqueue,
        isScrolling: () => scrolling,
        markIdle,
        markScroll,
        setSuspended,
    }
}
